import { LazyRender } from '@app/components/various/LazyRender';
import { Matrix } from '@app/components/various/Matrix';
import { filterEmptyVariants, getEstimatedProductMatrixHeight } from '@app/logic/sizeCharts';
import { useMatrixLayout } from '@app/utils/hooks/matrix/useMatrixLayout';
import { useMatrixPresets } from '@app/utils/hooks/matrix/useMatrixPresets';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import cx from 'classnames';
import React from 'react';

import { useMatrixColumnsContext, useMatrixQuantitiesContext, useMatrixSettingsContext, useMatrixVariantsContext } from './context';
import { MatrixOverflow } from './MatrixOverflow';
import { MatrixVariantInfo } from './MatrixVariantInfo';

const VIEWPORT_HEIGHT_DIVIDER = 2;

export const MatrixLazyRender = () => {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const { isDesktop, isReadOnly, isRRPEnabled, isInDetailsModal } = useMatrixSettingsContext();
  const { availableItemIds, variants: originalVariants, scannedBarcode } = useMatrixVariantsContext();
  const { cellQuantitiesMap } = useMatrixQuantitiesContext();
  const { canPinColumns } = useMatrixColumnsContext();
  const { hasMultipleRows } = useMatrixLayout();
  const { hasPresetsControls } = useMatrixPresets();

  const hasVisiblePresetControls = hasPresetsControls && !isDesktop;

  const variants = React.useMemo(() => {
    if (isReadOnly) {
      return filterEmptyVariants(cellQuantitiesMap, originalVariants);
    }

    return originalVariants;
  }, [cellQuantitiesMap, originalVariants, isReadOnly]);

  const estimatedHeight = getEstimatedProductMatrixHeight({
    hasControls: hasVisiblePresetControls || canPinColumns,
    hasSummaries: hasMultipleRows,
    isDesktop,
    variants,
  });

  const hasAvailableSizeByBarcode = React.useMemo(() => {
    return (
      isDefined(scannedBarcode) &&
      variants.flatMap(variant => variant.items).some(item => item.ean === scannedBarcode && availableItemIds.includes(item.item))
    );
  }, [availableItemIds, scannedBarcode, variants]);

  const shouldForceVisibility = hasAvailableSizeByBarcode && isInDetailsModal;

  if (isEmpty(variants)) {
    return null;
  }

  const [firstVariant] = variants;

  return (
    <div
      ref={ref}
      className={cx('my-2', {
        'cursor-not-allowed': isReadOnly,
      })}
    >
      {!isDesktop && isDefined(firstVariant) && <MatrixVariantInfo variant={firstVariant} shouldShowRrp={isRRPEnabled} />}

      <LazyRender
        forceVisible={shouldForceVisibility}
        estimatedHeight={estimatedHeight}
        rootMargin={`${window.screen.height / VIEWPORT_HEIGHT_DIVIDER}px 0px`}
      >
        <MatrixOverflow>
          <Matrix />
        </MatrixOverflow>
      </LazyRender>
    </div>
  );
};
